import React, { useCallback, useRef, useEffect } from 'react'
import { Map, Marker, GoogleApiWrapper, Circle } from 'google-maps-react'
import config from '../../config'

import colors from '../../styles/colors'

const MAP_STYLE = { width: '100%', height: '70vh' }
const CONTAINER_STYLE = { position: 'relative' }
const INITIAL_POSITION = { lat: -34.582903, lng: -58.44214 }
const ICON_LARGE = 1.4
const ICON_SMALL = 1.2
const DEFAULT_ZOOM = 13
const MAX_ZOOM = 18
const POIS_STYLE = [
  {
    featureType: 'poi.attraction',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.business',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.government',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.school',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.sports_complex',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
]

const MapContainer = ({ medics, google, mapCenterSearched, radius, onMapPositionChange, selectedMedicId, onSelectMarker, zoom = DEFAULT_ZOOM, mapCenter = INITIAL_POSITION }) => {
  const mapRef = useRef(null)
  const hasGoogleMaps = !!(google && google.maps)

  const onClickMarker = useCallback((_, marker) => {
    if (onSelectMarker) {
      onSelectMarker(marker.id)
    }
  }, [onSelectMarker])

  const adjustMapPosition = useCallback(
    (mapProps, map) => {
      const { google } = mapProps
      const bounds = new google.maps.LatLngBounds()
      let hasMarkers = false

      medics.forEach((medic) => {
        const lat = parseFloat(medic.Latitud)
        const lng = parseFloat(medic.Longitud)
        if (isNaN(lat) || isNaN(lng)) {
          return
        }
        hasMarkers = true
        bounds.extend(new google.maps.LatLng(lat, lng))
      })

      if (hasMarkers) {
        map.fitBounds(bounds)
      }
    },
    [medics]
  )

  const getIconMarker = useCallback(
    (isSelected) => {
      if (!hasGoogleMaps) {
        return null
      }

      return {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: isSelected ? colors.lightBlue : colors.blueOmint,
        fillOpacity: 1,
        scale: isSelected ? ICON_LARGE : ICON_SMALL,
        strokeWeight: 0,
        anchor: new google.maps.Point(14, 20),
        origin: new google.maps.Point(0, 0),
      }
    },
    [google, hasGoogleMaps]
  )

  const renderMarker = useCallback(
    (medic, index) => {
      const lat = parseFloat(medic.Latitud)
      const lng = parseFloat(medic.Longitud)
      if (isNaN(lat) || isNaN(lng)) {
        return null
      }
      const isSelected = selectedMedicId === medic.idTurno
      return (
        <Marker
          key={index}
          title={medic.nombreProfesional}
          id={medic.idTurno}
          name={medic.nombreProfesional}
          position={{ lat, lng }}
          onClick={onClickMarker}
          icon={getIconMarker(isSelected)}
        />
      )
    },
    [getIconMarker, onClickMarker, selectedMedicId]
  )

  const handleZoomChange = (_, map) =>
    onMapPositionChange(map.zoom, { lat: map.center.lat(), lng: map.center.lng() })

  const onMapReady = useCallback((mapProps, map) => {
    mapRef.current = map
    adjustMapPosition(mapProps, map)
  }, [adjustMapPosition])

  useEffect(() => {
    if (!hasGoogleMaps) {
      return
    }

    if (selectedMedicId && mapRef.current) {
      const medic = medics.find(m => m.idTurno === selectedMedicId)
      if (medic) {
        const lat = parseFloat(medic.Latitud)
        const lng = parseFloat(medic.Longitud)
        if (!isNaN(lat) && !isNaN(lng)) {
          mapRef.current.panTo({ lat, lng })
          mapRef.current.setZoom(16)
        }
      }
    }
  }, [selectedMedicId, medics, hasGoogleMaps])

  if (!hasGoogleMaps) {
    return <div style={MAP_STYLE} />
  }

  return (
    <Map
      google={google}
      zoom={zoom}
      maxZoom={MAX_ZOOM}
      style={MAP_STYLE}
      containerStyle={CONTAINER_STYLE}
      initialCenter={INITIAL_POSITION}
      center={mapCenter}
      mapTypeControl={false}
      streetViewControl={false}
      onZoom_changed={handleZoomChange}
      onDragend={handleZoomChange}
      onReady={onMapReady}
      styles={POIS_STYLE}
    >
      {medics.map((medic, index) =>
        renderMarker(medic, index)
      )}
      {mapCenterSearched && (
        <Circle
          center={mapCenterSearched}
          radius={radius}
          strokeColor="#CCCCCC"
          strokeOpacity={0.7}
          strokeWeight={1.5}
          fillColor="#CCCCCC"
          fillOpacity={0.15}
          clickable={false}
        />
      )}
    </Map>
  )
}

export default GoogleApiWrapper({ apiKey: config.apikey, language: 'es_419' })(
  React.memo(MapContainer)
)
 